import {gql} from 'graphql-request';
import {useGqlRequest} from '@/api/request';
import {AnalysisItem, DeliveryData, DeliveryTileData} from '@/map/@types';
import {AreaOverlayType} from '@/map/enums';
import {useQuery} from '@tanstack/react-query';
import {getAreaQueryVariables} from './variables';

export const useDeliveryData = (item: AnalysisItem) => {
  const query =
    item.type === AreaOverlayType.CIRCLE
      ? GET_DELIVERY_DATA_BY_POINT
      : item.type === AreaOverlayType.POLYGON
      ? GET_DELIVERY_DATA_BY_POLYGON
      : GET_DELIVERY_DATA_BY_HJD;

  const request = useGqlRequest({
    query,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: DeliveryData;
      }> => {
    const variant = getAreaQueryVariables(item);

    if (!variant) {
      return;
    }

    return request(variant);
  };

  const id = item?.id;

  const select = (data?: {result?: DeliveryData}) => data?.result;

  return useQuery({
    queryFn,
    queryKey: ['map', 'analytics', id, 'delivery'],
    enabled: !!id,
    select,
  });
};

export const useQueryDeliveryTiles = (item: AnalysisItem) => {
  const query =
    item.type === AreaOverlayType.CIRCLE
      ? GET_DELIVERY_TILE_BY_POINT
      : item.type === AreaOverlayType.POLYGON
      ? GET_DELIVERY_TILE_BY_POLYGON
      : GET_DELIVERY_TILE_BY_HJD;

  const request = useGqlRequest({
    query,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: DeliveryTileData[];
      }> => {
    const variant = getAreaQueryVariables(item);

    if (!variant) {
      return;
    }

    return request(variant);
  };

  const id = item?.id;

  const select = (data?: {result?: DeliveryTileData[]}) => data?.result;

  return useQuery({
    queryFn,
    queryKey: ['map', 'analytics', id, 'delivery-tile'],
    enabled: !!id,
    select,
  });
};

const DELEVERY_RESULT = `
  dates
  location
  merchants {
    baemin {
      purchaserCount
      transactionCount
      transactionSum
    }
    yogiyo {
      purchaserCount
      transactionCount
      transactionSum
    }
    coupangeats {
      purchaserCount
      transactionCount
      transactionSum
    }
  }
  total {
    purchaserCount
    transactionCount
    transactionSum
  }
`;

const GET_DELIVERY_DATA_BY_POINT = gql`
  query deliverySalesByPoint($lat: Float!, $lng: Float!, $radius: Float!) {
    result: deliverySalesByPoint(lat: $lat, lng: $lng, radius: $radius) {
      ${DELEVERY_RESULT}
    }
  }
`;

const GET_DELIVERY_DATA_BY_POLYGON = gql`
  query deliverySalesByPolygon($polygon: GeoPolygonArgs!) {
    result: deliverySalesByPolygon(polygon: $polygon) {
      ${DELEVERY_RESULT}
    }
  }
`;

const GET_DELIVERY_DATA_BY_HJD = gql`
  query deliverySalesByHjd($admCode: String!) {
    result: deliverySalesByHjd(admCode: $admCode) {
      ${DELEVERY_RESULT}
    }
  }
`;

const TILE_RESULT = `
  geometry {
    type
    coordinates
  }
  percent
  tileId
  totalTransactionCount
`;

const GET_DELIVERY_TILE_BY_POINT = gql`
  query deliverySalesBlockByPoint($lat: Float!, $lng: Float!, $radius: Float!) {
    result: deliverySalesBlockByPoint(lat: $lat, lng: $lng, radius: $radius) {
      ${TILE_RESULT}
    }
  }
`;

const GET_DELIVERY_TILE_BY_POLYGON = gql`
  query deliverySalesBlockByPolygon($polygon: GeoPolygonArgs!) {
    result: deliverySalesBlockByPolygon(polygon: $polygon) {
      ${TILE_RESULT}
    }
  }
`;

const GET_DELIVERY_TILE_BY_HJD = gql`
  query deliverySalesBlockByHjd($admCode: String!) {
    result: deliverySalesBlockByHjd(admCode: $admCode) {
      ${TILE_RESULT}
    }
  }
`;
